import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { mapRppgQualityReason } from 'lib/medsi/mapRppgQualityReasons';
import type { RppgCaptureQuality } from 'services/rppg/api';

const MAX_STARS = 5;

interface CaptureQualityBadgeProps {
  quality: RppgCaptureQuality;
  showGuidance?: boolean;
  compact?: boolean;
}

function CaptureQualityBadgeImpl({
  quality,
  showGuidance = true,
  compact = false,
}: CaptureQualityBadgeProps) {
  const { t } = useTranslation();
  const filled = Math.max(
    0,
    Math.min(MAX_STARS, Math.round(Number(quality.stars) || 0)),
  );
  const rawScore = Number(quality.score);
  const score =
    quality.score != null && !Number.isNaN(rawScore)
      ? Math.round(rawScore <= 1 ? rawScore * 100 : rawScore)
      : null;

  const [primaryReason] = quality.reasons ?? [];
  const guidance =
    showGuidance && primaryReason ? mapRppgQualityReason(primaryReason) : null;

  return (
    <div
      className={
        compact
          ? 'flex w-full flex-col items-center gap-0.5'
          : 'flex w-full flex-col items-center gap-1'
      }
    >
      <p className={compact ? 'text-[10px] font-medium text-slate-500' : 'text-xs font-medium text-slate-500'}>
        {t('medsi_capture_quality_label')}
      </p>
      <div className='flex items-center gap-0.5'>
        {Array.from({ length: MAX_STARS }, (_, index) => (
          <span
            key={`star-${index}`}
            className={`${compact ? 'text-sm' : 'text-lg'} leading-none ${
              index < filled ? 'text-[#1A6AD7]' : 'text-slate-300'
            }`}
            aria-hidden
          >
            ★
          </span>
        ))}
        {score != null ? (
          <span
            className={
              compact
                ? 'ml-1.5 text-xs font-semibold text-slate-900'
                : 'ml-2 text-sm font-semibold text-slate-900'
            }
          >
            {score}%
          </span>
        ) : null}
      </div>

      {guidance ? (
        <div className='w-full px-1 text-center'>
          <p
            className={
              compact
                ? 'truncate text-xs font-semibold text-amber-700'
                : 'truncate text-sm font-semibold text-amber-700'
            }
          >
            {guidance.header}
          </p>
          {!compact ? (
            <p className='line-clamp-2 text-xs text-slate-500'>
              {guidance.description}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export const CaptureQualityBadge = memo(CaptureQualityBadgeImpl);
